var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback } from 'react';
import { MessageSimple } from './MessageSimple';
import { useActionHandler, useDeleteHandler, useEditHandler, useFlagHandler, useMentionsHandler, useMuteHandler, useOpenThreadHandler, usePinHandler, useReactionHandler, useRetryHandler, useUserHandler, useUserRole, } from './hooks';
import { areMessagePropsEqual, defaultPinPermissions, getMessageActions, MESSAGE_ACTIONS, } from './utils';
import { useChannelContext } from '../../context/ChannelContext';
var MessageWithContext = function (props) {
    var canPin = props.canPin, channel = props.channel, formatDate = props.formatDate, _a = props.groupStyles, groupStyles = _a === void 0 ? [] : _a, handleAction = props.handleAction, handleDelete = props.handleDelete, handleFlag = props.handleFlag, handleMute = props.handleMute, handleOpenThread = props.handleOpenThread, handlePin = props.handlePin, handleReaction = props.handleReaction, handleRetry = props.handleRetry, _b = props.Message, MessageUIComponent = _b === void 0 ? MessageSimple : _b, message = props.message, _c = props.messageActions, messageActions = _c === void 0 ? Object.keys(MESSAGE_ACTIONS) : _c, onMentionsClickMessage = props.onMentionsClickMessage, onMentionsHoverMessage = props.onMentionsHoverMessage, propOnUserClick = props.onUserClick, propOnUserHover = props.onUserHover, userRoles = props.userRoles;
    var channelConfig = channel.getConfig && channel.getConfig();
    var _d = useEditHandler(), clearEdit = _d.clearEdit, editing = _d.editing, setEdit = _d.setEdit;
    var _e = useUserHandler(message, {
        onUserClickHandler: propOnUserClick,
        onUserHoverHandler: propOnUserHover,
    }), onUserClick = _e.onUserClick, onUserHover = _e.onUserHover;
    var isAdmin = userRoles.isAdmin, isModerator = userRoles.isModerator, isMyMessage = userRoles.isMyMessage;
    var canEdit = isMyMessage || isModerator || isAdmin;
    var canDelete = canEdit;
    var canReact = true;
    var canReply = true;
    var messageActionsHandler = useCallback(function () {
        if (!message || !messageActions) {
            return [];
        }
        return getMessageActions(messageActions, {
            canDelete: canDelete,
            canEdit: canEdit,
            canFlag: !isMyMessage,
            canMute: !isMyMessage && !!(channelConfig === null || channelConfig === void 0 ? void 0 : channelConfig.mutes),
            canPin: canPin,
            canReact: canReact,
            canReply: canReply,
        });
    }, [
        canDelete,
        canEdit,
        canPin,
        canReply,
        canReact,
        channelConfig === null || channelConfig === void 0 ? void 0 : channelConfig.mutes,
        isMyMessage,
        message,
        messageActions,
    ]);
    var actionsEnabled = message && message.type === 'regular' && message.status === 'received';
    return (React.createElement(MessageUIComponent, __assign({}, props, { actionsEnabled: actionsEnabled, channelConfig: channelConfig, clearEditingState: clearEdit, editing: editing, formatDate: formatDate, getMessageActions: messageActionsHandler, groupStyles: groupStyles, handleAction: handleAction, handleDelete: handleDelete, handleEdit: setEdit, handleFlag: handleFlag, handleMute: handleMute, handleOpenThread: handleOpenThread, handlePin: handlePin, handleReaction: handleReaction, handleRetry: handleRetry, isMyMessage: function () { return isMyMessage; }, onMentionsClickMessage: onMentionsClickMessage, onMentionsHoverMessage: onMentionsHoverMessage, onUserClick: onUserClick, onUserHover: onUserHover, setEditingState: setEdit })));
};
var MemoizedMessage = React.memo(MessageWithContext, areMessagePropsEqual);
/**
 * Message - A high level component which implements all the logic required for a Message.
 * The actual rendering of the Message is delegated via the "Message" property.
 * @example ./Message.md
 */
export var Message = function (props) {
    var addNotification = props.addNotification, propChannel = props.channel, getFlagMessageErrorNotification = props.getFlagMessageErrorNotification, getFlagMessageSuccessNotification = props.getFlagMessageSuccessNotification, getMuteUserErrorNotification = props.getMuteUserErrorNotification, getMuteUserSuccessNotification = props.getMuteUserSuccessNotification, getPinMessageErrorNotification = props.getPinMessageErrorNotification, message = props.message, propOnMentionsClick = props.onMentionsClick, propOnMentionsHover = props.onMentionsHover, propOpenThread = props.openThread, _a = props.pinPermissions, pinPermissions = _a === void 0 ? defaultPinPermissions : _a, propRetrySendMessage = props.retrySendMessage;
    var contextChannel = useChannelContext().channel;
    var channel = propChannel || contextChannel;
    var handleAction = useActionHandler(message);
    var handleDelete = useDeleteHandler(message);
    var handleOpenThread = useOpenThreadHandler(message, propOpenThread);
    var handleReaction = useReactionHandler(message);
    var handleRetry = useRetryHandler(propRetrySendMessage);
    var userRoles = useUserRole(message);
    var handleFlag = useFlagHandler(message, {
        getErrorNotification: getFlagMessageErrorNotification,
        getSuccessNotification: getFlagMessageSuccessNotification,
        notify: addNotification,
    });
    var handleMute = useMuteHandler(message, {
        getErrorNotification: getMuteUserErrorNotification,
        getSuccessNotification: getMuteUserSuccessNotification,
        notify: addNotification,
    });
    var _b = useMentionsHandler(message, {
        onMentionsClick: propOnMentionsClick,
        onMentionsHover: propOnMentionsHover,
    }), onMentionsClick = _b.onMentionsClick, onMentionsHover = _b.onMentionsHover;
    var _c = usePinHandler(message, pinPermissions, {
        getErrorNotification: getPinMessageErrorNotification,
        notify: addNotification,
    }), canPin = _c.canPin, handlePin = _c.handlePin;
    return (React.createElement(MemoizedMessage, __assign({}, props, { canPin: canPin, channel: channel, handleAction: handleAction, handleDelete: handleDelete, handleFlag: handleFlag, handleMute: handleMute, handleOpenThread: handleOpenThread, handlePin: handlePin, handleReaction: handleReaction, handleRetry: handleRetry, onMentionsClickMessage: onMentionsClick, onMentionsHoverMessage: onMentionsHover, userRoles: userRoles })));
};
